import { AnimatePresence, motion } from 'motion/react';
import { useMemo } from 'react';

import { VFlex } from '../VFlex';
import { VStack } from '../VStack';
import { useVTheme } from '../VThemeProvider';
import { ArrowDropDown, ArrowRight } from '../asset';
import { useVTreeContext } from './hook';
import style from './style';
import type { VTreeNodeProps } from './type';

const VTreeNodeLabel = ({ depth = 0, ...node }: VTreeNodeProps) => {
  const { themeName } = useVTheme();
  const {
    rounded,
    renderNode,
    getNodeStatus,

    onSelectNode,
    onToggleIcon,
  } = useVTreeContext();

  const status = useMemo(() => getNodeStatus(node), [node, getNodeStatus]);

  return (
    <VFlex
      role="presentation"
      style={{
        paddingLeft: `${depth * 20 + 4}px`,
      }}
      className={style.vTreeItem({ status, rounded, themeName }).name}
      items="center"
      gap={0.25}
      onClick={() => onSelectNode(node)}
      data-testid={`v-tree-node-${node.id}-label`}
    >
      <VFlex className="flex-1" items="center">
        <VFlex
          role="button"
          items="center"
          className="z-[11]"
          onClickCapture={e => {
            e.stopPropagation();

            onToggleIcon(node);
          }}
          data-testid={`v-tree-node-${node.id}-label-btn-expand`}
        >
          {!status.expanded ? <ArrowRight /> : <ArrowDropDown />}
        </VFlex>
        <div
          aria-label={node.label}
          className="w-fit flex-1 text-ellipsis whitespace-nowrap"
          data-testid={`v-tree-node-${node.id}-label-text`}
        >
          {typeof renderNode === 'function'
            ? renderNode({ ...node, ...status })
            : node.label}
        </div>
      </VFlex>
    </VFlex>
  );
};

const VTreeNode = (node: VTreeNodeProps) => {
  const { rounded, isExpandedNode, getNodeStatus } = useVTreeContext();
  const depth = node.depth ?? 0;
  const status = useMemo(() => getNodeStatus(node), [node, getNodeStatus]);

  return (
    <VStack
      aria-disabled={status.disabled}
      role="treeitem"
      className={style.vTreeItem({ status, rounded }).container}
      data-testid={`v-tree-node-${node.id}`}
      data-nodeid={node.id}
    >
      <VTreeNodeLabel {...node} depth={depth} />

      <AnimatePresence>
        {isExpandedNode(node) &&
          node.children?.map(childItem => (
            <motion.div
              initial="hidden"
              animate="visible"
              exit="hidden"
              key={childItem.id}
              role="group"
              variants={{
                visible: {
                  height: 'auto',
                  opacity: 1,
                },
                hidden: {
                  height: '0px',
                  opacity: 0,
                },
              }}
              transition={{
                duration: 0.25,
                ease: [0.23, 1, 0.32, 1],
              }}
            >
              <VTreeNode {...childItem} depth={depth + 1} />
            </motion.div>
          ))}
      </AnimatePresence>
    </VStack>
  );
};

export default VTreeNode;
